/* Which LC/Dispatch dashboard cards pool their named layouts ("Views").
 *
 * Every card the user creates through FirstCardModal is keyed `card-${Date.now()}`
 * at creation time, so its TanTable settingsKey is e.g.
 * `lc-card-card-1786379601919`. A View saved there used to be keyed to that one
 * card's timestamp and invisible on every other card, even though all custom cards
 * are the same component over the same orders field registry.
 *
 * ONE POOL FOR EVERYTHING. LC and Dispatch custom cards share a single key, and so
 * do the table and Multi Row views of a card — a View saved anywhere on either
 * dashboard is offered everywhere on both. That is a deliberate call: the two
 * dashboards draw from the same orders field registry, and John wanted one list
 * rather than one per dashboard.
 *   The cost, accepted knowingly: a Multi Row layout and a table layout render from
 *   genuinely different column sets (proCardColumns vs the LC bucket columns), so
 *   loading one into the other applies as a mostly-empty layout. `layoutModeLabel`
 *   in TableTemplateModals exists to at least LABEL which is which, using the mode
 *   stamped into each snapshot — the pooled settings_key can no longer tell them
 *   apart the way `lc-card-` vs `lc-cards-` used to.
 *
 * BUILT-IN cards carry hand-written keys instead (`past-due`, `no-outgate`,
 * `schedule-drill-melrose`, ...) and deliberately keep their own per-card pool:
 * each has a curated column set for one purpose, and a general-purpose card layout
 * should not be interchangeable with it.
 *
 * The key prefix is the only signal either dashboard has — neither stores an
 * is-custom flag on the card config — so this is the one place that knows the
 * convention. If a built-in card is ever named `card-…`, it would silently join
 * the pool; the CUSTOM_CARD_KEY_PREFIX constant is here so that stays greppable. */

export const CUSTOM_CARD_KEY_PREFIX = 'card-';

/**
 * The single pooled layout key shared by every custom card on both dashboards.
 * Migration 386 folded the earlier per-dashboard keys (`lc-card-custom`,
 * `lc-cards-custom`, `dispatch-card-custom`) into this one.
 */
export const CUSTOM_CARD_POOL_KEY = 'dashboard-card-custom';

/** True for a user-created custom card, false for a built-in one. */
export function isCustomCardKey(cardKey: string | null | undefined): boolean {
  return !!cardKey && cardKey.startsWith(CUSTOM_CARD_KEY_PREFIX);
}

/**
 * The pooled layout key for a custom card, or undefined for a built-in one (which
 * keeps its own per-card pool). Undefined is what makes TanTable fall back to
 * `settingsKey`.
 */
export function customCardScopeKey(cardKey: string | null | undefined): string | undefined {
  return isCustomCardKey(cardKey) ? CUSTOM_CARD_POOL_KEY : undefined;
}
